import React from 'react';
import { MoreVertical, CalendarCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { OfficialAvatar } from './StatusPill';

export const AccountPill = ({ o }) => {
  const [label, cls] = o.has_account ? ['Joined', 'bg-emerald-50 text-emerald-700 border-emerald-200']
    : o.invited_at ? ['Invited', 'bg-amber-50 text-amber-800 border-amber-200'] : ['Not invited', 'bg-slate-50 text-slate-500 border-slate-200'];
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold whitespace-nowrap ${cls}`} data-testid={`official-account-${o.id}`}>{label}</span>;
};

export const Tags = ({ items, tone }) => (items?.length ? (
  <div className="flex flex-wrap gap-1">
    {items.map(t => <span key={t} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${tone}`}>{t}</span>)}
  </div>
) : null);

export const OfficialRow = ({ o, selected, onSelect, roleLabel, onOpen, onEdit, onInvite, onDelete }) => (
  <div className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b last:border-b-0 hover:bg-slate-50/70 transition-colors" data-testid={`official-row-${o.id}`}>
    <Checkbox checked={selected} onCheckedChange={() => onSelect(o.id)} data-testid={`official-select-${o.id}`} />
    <button type="button" onClick={() => onOpen(o)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`official-open-${o.id}`}>
      <OfficialAvatar name={o.full_name} photo={o.photo_path} size="w-10 h-10" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-semibold truncate ${o.status === 'inactive' ? 'text-slate-400' : 'text-slate-900'}`} data-testid={`official-name-${o.id}`}>{o.full_name}</span>
          {o.status === 'inactive' && <span className="text-[10px] uppercase font-bold text-slate-400">Inactive</span>}
        </div>
        <div className="text-xs text-slate-500 truncate">{[o.email, o.phone].filter(Boolean).join(' · ') || 'No contact details'}</div>
        <div className="mt-1 sm:hidden"><AccountPill o={o} /></div>
      </div>
    </button>
    <div className="hidden md:block w-56 space-y-1">
      <Tags items={o.sports} tone="bg-[#00247D]/10 text-[#00247D]" />
      <Tags items={(o.positions || []).map(roleLabel)} tone="bg-slate-100 text-slate-600" />
    </div>
    <div className="hidden lg:block w-40 text-xs text-slate-500 truncate" title={(o.certifications || []).join(', ')}>{(o.certifications || []).join(', ') || '—'}</div>
    <div className="hidden sm:flex flex-col items-end gap-1 w-28">
      <AccountPill o={o} />
      {o.availability_submitted && <span className="text-[10px] text-emerald-700 flex items-center gap-1"><CalendarCheck className="w-3 h-3" />Availability set</span>}
    </div>
    <div className="w-14 text-center" data-testid={`official-upcoming-${o.id}`}>
      <div className="text-lg font-black text-[#00247D] leading-none" style={{ fontFamily: 'Barlow Condensed' }}>{o.upcoming_assignments || 0}</div>
      <div className="text-[9px] uppercase text-slate-400 font-bold">Upcoming</div>
    </div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="p-1.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`official-menu-${o.id}`}><MoreVertical className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onOpen(o)}>View details</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(o)} data-testid={`official-edit-${o.id}`}>Edit</DropdownMenuItem>
        {!o.has_account && o.email && <DropdownMenuItem onClick={() => onInvite(o)} data-testid={`official-invite-${o.id}`}>{o.invited_at ? 'Resend invite' : 'Send invite'}</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onDelete(o)} className="text-red-600" data-testid={`official-delete-${o.id}`}>Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);
